import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import {Box, Colors, IconButton} from "@bounce/shared/ui";
import {IconPanelLeftClose} from "../../assets";
import {FILTER_PANEL_SHORTCUT, FilterPanelState} from "../../hooks/useFilterPanel";
import {Button} from "./Button";
import {Tooltip} from "./Tooltip";

export const FILTER_PANEL_CLASS = "filter-panel";

const HIDE_FILTERS_LABEL = "Hide filters";
const SHOW_FILTERS_LABEL = "Show filters";
const FILTERS_BUTTON_LABEL = "Filters";
const FILTERS_BUTTON_HEIGHT = 40;
const COLLAPSE_BUTTON_SIZE = 32;
const PANEL_ICON_SIZE = 18;
const BADGE_SIZE = 18;
const FADE_DURATION = "120ms";

const reducedMotionSx = {
  "@media (prefers-reduced-motion: reduce)": {transition: "none", animation: "none"},
} as const;

export const FilterLinesIcon = ({size = PANEL_ICON_SIZE}: {size?: number}) => (
  <FilterListRoundedIcon sx={{fontSize: size, color: "inherit"}} />
);

const ShortcutTooltipTitle = ({label}: {label: string}) => (
  <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
    {label}
    <Box
      component="kbd"
      sx={{
        px: 0.75,
        minWidth: BADGE_SIZE,
        textAlign: "center",
        borderRadius: "4px",
        border: `1px solid ${Colors.tertiary._300}`,
        backgroundColor: Colors.tertiary._100,
        fontFamily: "inherit",
        fontSize: 12,
        color: Colors.grey._700,
      }}
    >
      {FILTER_PANEL_SHORTCUT}
    </Box>
  </Box>
);

export const FilterPanelCollapseButton = ({panel}: {panel: FilterPanelState}) => (
  <Tooltip title={<ShortcutTooltipTitle label={HIDE_FILTERS_LABEL} />} placement="right">
    <IconButton
      ref={panel.collapseButtonRef}
      onClick={panel.toggle}
      aria-label={HIDE_FILTERS_LABEL}
      aria-expanded
      aria-controls={panel.panelId}
      disableRipple
      sx={{
        width: COLLAPSE_BUTTON_SIZE,
        height: COLLAPSE_BUTTON_SIZE,
        p: 0,
        borderRadius: "8px",
        color: Colors.grey._600,
        opacity: 0,
        transition: `opacity ${FADE_DURATION} ease, background-color ${FADE_DURATION} ease`,
        "&:hover": {backgroundColor: Colors.tertiary._300, color: Colors.grey._900},
        [`.${FILTER_PANEL_CLASS}:hover &, &:focus-visible`]: {opacity: 1},
        "&:focus-visible": {outline: `2px solid ${Colors.blue._500}`, outlineOffset: "-2px"},
        "@media (hover: none)": {opacity: 1},
        ...reducedMotionSx,
      }}
    >
      <IconPanelLeftClose width={PANEL_ICON_SIZE} height={PANEL_ICON_SIZE} />
    </IconButton>
  </Tooltip>
);

type FiltersToggleButtonProps = {
  panel: FilterPanelState;
  activeCount: number;
};

export const FiltersToggleButton = ({panel, activeCount}: FiltersToggleButtonProps) => {
  if (panel.isExpanded) return null;

  const ariaLabel = activeCount > 0 ? `${SHOW_FILTERS_LABEL}, ${activeCount} active` : SHOW_FILTERS_LABEL;

  return (
    <Tooltip title={<ShortcutTooltipTitle label={SHOW_FILTERS_LABEL} />} placement="bottom">
      <Box
        ref={panel.showButtonWrapperRef}
        component="span"
        sx={{
          display: "inline-flex",
          "@keyframes filtersButtonFadeIn": {from: {opacity: 0}, to: {opacity: 1}},
          animation: `filtersButtonFadeIn ${FADE_DURATION} ease`,
          ...reducedMotionSx,
        }}
      >
        <Button
          buttonType="outlined-secondary"
          size="small"
          onClick={panel.toggle}
          aria-label={ariaLabel}
          aria-expanded={false}
          aria-controls={panel.panelId}
          startIcon={<FilterLinesIcon />}
          sx={{
            height: FILTERS_BUTTON_HEIGHT,
            color: Colors.grey._900,
            borderColor: Colors.tertiary._300,
            backgroundColor: Colors.bw.white,
            "&:hover": {borderColor: Colors.grey._400, backgroundColor: Colors.tertiary._100},
            "& .MuiButton-startIcon": {mr: 0, ml: 0},
          }}
        >
          {FILTERS_BUTTON_LABEL}
          {activeCount > 0 && (
            <Box
              component="span"
              sx={{
                minWidth: BADGE_SIZE,
                height: BADGE_SIZE,
                px: 0.5,
                borderRadius: `${BADGE_SIZE / 2}px`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: Colors.blue._700,
                color: Colors.bw.white,
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              {activeCount}
            </Box>
          )}
        </Button>
      </Box>
    </Tooltip>
  );
};
